import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import BpsGlobalLogo from '../Logo/BpsGlobal.jpeg';

// Helper function to load image as base64
const loadImageAsBase64 = (imagePath) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/jpeg'));
    };
    img.onerror = reject;
    img.src = imagePath;
  });
};

export const generateSaleReturnPDF = async (saleReturnData, companyDetails, items) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // BPS Global Trading color scheme - matching logo
  const bpsBlue = [0, 102, 204]; // Primary blue from logo
  const bpsDarkBlue = [0, 70, 150]; // Darker blue for gradient
  const darkGray = [52, 58, 64];

  // Header with white background
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pageWidth, 45, 'F');
  
  // Add a subtle border at the bottom of header
  doc.setDrawColor(...bpsBlue);
  doc.setLineWidth(0.5);
  doc.line(0, 45, pageWidth, 45);

  // Company Logo - Load and add BpsGlobal.jpeg
  try {
    const logoBase64 = await loadImageAsBase64(BpsGlobalLogo);
    doc.addImage(logoBase64, 'JPEG', 15, 10, 25, 25);
  } catch (error) {
    console.error('Error loading logo:', error);
    // Fallback to placeholder if logo fails to load
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(15, 10, 25, 25, 3, 3, 'F');
    doc.setFontSize(16);
    doc.setTextColor(0, 102, 204);
    doc.setFont('helvetica', 'bold');
    doc.text('LOGO', 20, 25);
  }

  // Company Name and Details
  doc.setFontSize(22);
  doc.setTextColor(...bpsBlue);
  doc.setFont('helvetica', 'bold');
  doc.text(companyDetails.companyName || 'BPS Global Trading', 45, 20);
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkGray);
  doc.text(companyDetails.address || 'Dubai, United Arab Emirates', 45, 27);
  doc.text(`TRN: ${companyDetails.trn || companyDetails.vatNumber || 'N/A'}`, 45, 32);
  doc.text(`Tel: ${companyDetails.phone || '+971-XX-XXXXXXX'} | Email: ${companyDetails.email || 'info@company.ae'}`, 45, 37);

  // Credit Note / Sales Return Text
  doc.setFontSize(14);
  doc.setTextColor(220, 53, 69); // Red color for CREDIT NOTE
  doc.setFont('helvetica', 'bold');
  doc.text('CREDIT NOTE', pageWidth - 52.5, 18);

  // Invoice Details Box
  const invoiceBoxY = 50;
  doc.setFillColor(248, 249, 250);
  doc.roundedRect(15, invoiceBoxY, pageWidth - 30, 46, 2, 2, 'F');
  doc.setDrawColor(...bpsBlue);
  doc.setLineWidth(0.5);
  doc.roundedRect(15, invoiceBoxY, pageWidth - 30, 46, 2, 2, 'S');

  // Left side - Customer Details
  doc.setFontSize(10);
  doc.setTextColor(...darkGray);
  doc.setFont('helvetica', 'bold');
  doc.text('RETURN FROM:', 20, invoiceBoxY + 7);
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...bpsBlue);
  doc.text(saleReturnData.CustomerName || saleReturnData.MasterName || 'Customer Name', 20, invoiceBoxY + 13);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...darkGray);
  const customerAddress = saleReturnData.ShipToAddress1 || 'Customer Address';
  doc.text(customerAddress.substring(0, 40), 20, invoiceBoxY + 18);
  if (saleReturnData.ShipToAddress2) {
    doc.text(saleReturnData.ShipToAddress2.substring(0, 40), 20, invoiceBoxY + 23);
  }
  doc.text(`TRN: ${saleReturnData.VATNumber || 'N/A'}`, 20, invoiceBoxY + 28);

  // Right side - Return Info
  const rightX = pageWidth - 70;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...darkGray);
  
  doc.text('Credit Note No:', rightX, invoiceBoxY + 7);
  doc.text('Return Date:', rightX, invoiceBoxY + 13);
  doc.text('Original Invoice:', rightX, invoiceBoxY + 19);
  doc.text('Invoice Type:', rightX, invoiceBoxY + 25);
  doc.text('Currency Type:', rightX, invoiceBoxY + 31);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...bpsBlue);
  const billNo = saleReturnData.BillPrefix && saleReturnData.FinYear 
    ? `${saleReturnData.BillPrefix}/${saleReturnData.BillNumber}/${saleReturnData.FinYear}`
    : saleReturnData.BillNumber || 'N/A';
  doc.text(billNo, rightX + 30, invoiceBoxY + 7);
  
  const billDate = saleReturnData.BillDate 
    ? new Date(saleReturnData.BillDate).toLocaleDateString('en-GB')
    : 'N/A';
  doc.text(billDate, rightX + 30, invoiceBoxY + 13);
  doc.text(saleReturnData.SaleNo || 'N/A', rightX + 30, invoiceBoxY + 19);
  doc.text(saleReturnData.InvoiceTypeName || 'N/A', rightX + 30, invoiceBoxY + 25);
  doc.text('AED', rightX + 30, invoiceBoxY + 31);

  // Items Table
  const tableStartY = invoiceBoxY + 53;
  
  const tableData = (saleReturnData.saleReturnDetails || []).map((item, index) => {
    const itemName = items.find(i => i.Id === item.ItemId)?.Name || item.ItemName || 'Item';
    return [
      index + 1,
      itemName,
      item.HSNCode || '-',
      parseFloat(item.Quantity || 0).toFixed(2),
      `AED ${parseFloat(item.Rate || 0).toFixed(2)}`,
      `${parseFloat(item.VATRate || item.GSTRate || 0).toFixed(0)}%`,
      `AED ${parseFloat(item.TaxableAmount || 0).toFixed(2)}`,
      `AED ${parseFloat(item.VATAmount || 0).toFixed(2)}`,
      `AED ${parseFloat(item.TotalAmount || 0).toFixed(2)}`
    ];
  });

  autoTable(doc, {
    startY: tableStartY,
    head: [[
      '#',
      'Description',
      'HSN',
      'Qty',
      'Rate',
      'VAT%',
      'Taxable',
      'VAT Amt',
      'Total'
    ]],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: bpsBlue,
      textColor: [255, 255, 255],
      fontSize: 8,
      fontStyle: 'bold',
      halign: 'center',
      valign: 'middle',
      lineWidth: 0.1,
      lineColor: [255, 255, 255]
    },
    bodyStyles: {
      fontSize: 7.5,
      textColor: [52, 58, 64],
      lineWidth: 0.1,
      lineColor: [200, 200, 200]
    },
    columnStyles: {
      0: { halign: 'center', cellWidth: 8 },
      1: { halign: 'left', cellWidth: 48 },
      2: { halign: 'center', cellWidth: 15 },
      3: { halign: 'center', cellWidth: 12 },
      4: { halign: 'right', cellWidth: 20 },
      5: { halign: 'center', cellWidth: 12 },
      6: { halign: 'right', cellWidth: 22 },
      7: { halign: 'right', cellWidth: 20 },
      8: { halign: 'right', cellWidth: 23 }
    },
    alternateRowStyles: {
      fillColor: [248, 249, 250]
    },
    margin: { left: 15, right: 15 }
  });

  // Summary Box - Calculate height based on whether payment details exist
  const summaryY = doc.lastAutoTable.finalY + 5;
  const summaryBoxWidth = 75;
  const summaryBoxX = pageWidth - summaryBoxWidth - 15;
  
  // Terms and Conditions - position FIRST (at the top left)
  const termsY = summaryY;
  doc.setFillColor(248, 249, 250);
  doc.rect(15, termsY, pageWidth - summaryBoxWidth - 25, 25, 'F');
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...darkGray);
  doc.text('Terms & Conditions:', 20, termsY + 66);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('1. This credit note is valid for 30 days from the date of issue.', 20, termsY + 71);
  doc.text('2. Credit amount will be adjusted against future purchases.', 20, termsY + 75);
  doc.text('3. Returned goods must be in original condition.', 20, termsY + 79);

  // Summary Box - position on the RIGHT side
  // Calculate box height dynamically
  let boxHeight = 73; // Base height for summary items
  if (saleReturnData.CashAmount || saleReturnData.UPIAmount || saleReturnData.CardAmount) {
    let paymentLines = 0;
    if (saleReturnData.CashAmount > 0) paymentLines++;
    if (saleReturnData.UPIAmount > 0) paymentLines++;
    if (saleReturnData.CardAmount > 0) paymentLines++;
    if (saleReturnData.BalanceAmount > 0) paymentLines++;
    boxHeight += 9 + (paymentLines * 5); // Minimal padding after last line
  }

  // Summary background
  doc.setFillColor(248, 249, 250);
  doc.roundedRect(summaryBoxX, summaryY, summaryBoxWidth, boxHeight, 2, 2, 'F');
  doc.setDrawColor(...bpsBlue);
  doc.setLineWidth(0.3);
  doc.roundedRect(summaryBoxX, summaryY, summaryBoxWidth, boxHeight, 2, 2, 'S');

  let currentY = summaryY + 7;
  const labelX = summaryBoxX + 5;
  const currencyX = summaryBoxX + summaryBoxWidth - 35; // Fixed position for "AED"
  const valueX = summaryBoxX + summaryBoxWidth - 5;

  // Summary items
  const summaryItems = [
    { label: 'Subtotal:', value: parseFloat(saleReturnData.TaxableAmount || 0).toFixed(2) },
    { label: 'VAT Amount:', value: parseFloat(saleReturnData.VATAmount || 0).toFixed(2) },
    { label: 'Freight:', value: parseFloat(saleReturnData.FreightAmount || 0).toFixed(2) },
    { label: 'Freight VAT:', value: parseFloat(saleReturnData.FreightVATAmount || 0).toFixed(2) },
    { label: 'Packing:', value: parseFloat(saleReturnData.PackingAmount || 0).toFixed(2) },
    { label: 'Packing VAT:', value: parseFloat(saleReturnData.PackingVATAmount || 0).toFixed(2) },
    { label: 'Round Off:', value: parseFloat(saleReturnData.RoundOff || 0).toFixed(2) }
  ];

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkGray);

  summaryItems.forEach(item => {
    doc.text(item.label, labelX, currentY);
    doc.text(item.value, valueX, currentY, { align: 'right' });
    currentY += 6;
  });

  // Divider line
  doc.setDrawColor(...bpsBlue);
  doc.setLineWidth(0.5);
  doc.line(summaryBoxX + 5, currentY, summaryBoxX + summaryBoxWidth - 5, currentY);
  currentY += 7;

  // Grand Total
  doc.setFillColor(...bpsBlue);
  doc.rect(summaryBoxX, currentY - 5, summaryBoxWidth, 12, 'F');
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('CREDIT TOTAL:', labelX, currentY + 2);
  doc.text('AED', currencyX, currentY + 2);
  doc.text(parseFloat(saleReturnData.TotalAmount || 0).toFixed(2), valueX, currentY + 2, { align: 'right' });

  // Payment Details (if available) - inside the box
  if (saleReturnData.CashAmount || saleReturnData.UPIAmount || saleReturnData.CardAmount) {
    currentY += 12;       
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkGray);
    doc.text('Refund Details:', labelX, currentY);
    currentY += 5;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    if (saleReturnData.CashAmount > 0) {
      doc.setTextColor(...darkGray);
      doc.text('Cash:', labelX, currentY);
      doc.text(parseFloat(saleReturnData.CashAmount).toFixed(2), valueX, currentY, { align: 'right' });
      currentY += 5;
    }
    if (saleReturnData.UPIAmount > 0) {
      doc.setTextColor(...darkGray);
      doc.text('UPI:', labelX, currentY);
      doc.text(parseFloat(saleReturnData.UPIAmount).toFixed(2), valueX, currentY, { align: 'right' });
      currentY += 5;
    }
    if (saleReturnData.CardAmount > 0) {
      doc.setTextColor(...darkGray);
      doc.text('Card:', labelX, currentY);
      doc.text(parseFloat(saleReturnData.CardAmount).toFixed(2), valueX, currentY, { align: 'right' });
      currentY += 5;
    }
    if (saleReturnData.BalanceAmount > 0) {
      doc.setTextColor(220, 53, 69);
      doc.setFont('helvetica', 'bold');
      doc.text('Balance:', labelX, currentY);
      doc.text(parseFloat(saleReturnData.BalanceAmount).toFixed(2), valueX, currentY, { align: 'right' });
    }
  }

  // Footer
  const footerY = pageHeight - 80;
  doc.setDrawColor(...bpsBlue);
  doc.setLineWidth(0.5);
  doc.line(15, footerY, pageWidth - 15, footerY);

  // Signature section
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...darkGray);
  doc.text('Authorized Signature', 20, footerY + 10);
  doc.text('Customer Signature', pageWidth - 60, footerY + 10);

  // Footer text
  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(100, 100, 100);
  doc.text('This is a computer-generated credit note and does not require a signature.', pageWidth / 2, footerY + 18, { align: 'center' });

  return doc;
};

export const downloadSaleReturnPDF = async (saleReturnData, companyDetails, items) => {
  const doc = await generateSaleReturnPDF(saleReturnData, companyDetails, items);
  const billNo = saleReturnData.BillPrefix && saleReturnData.FinYear 
    ? `${saleReturnData.BillPrefix}_${saleReturnData.BillNumber}_${saleReturnData.FinYear}`
    : `CreditNote_${saleReturnData.BillNumber}`;
  doc.save(`${billNo}.pdf`);
};

export const printSaleReturnPDF = async (saleReturnData, companyDetails, items) => {
  const doc = await generateSaleReturnPDF(saleReturnData, companyDetails, items);
  
  // Method 1: Try using autoPrint (works in most browsers)
  doc.autoPrint();
  
  // Open in new window
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  
  // Open PDF in new window which will trigger print dialog
  const printWindow = window.open(pdfUrl, '_blank');
  
  if (printWindow) {
    printWindow.onload = function() {
      // Trigger print after PDF loads
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };
    
    // Clean up URL after some time
    setTimeout(() => {
      URL.revokeObjectURL(pdfUrl);
    }, 10000);
  } else {
    // Fallback: If popup blocked, download the PDF
    const billNo = saleReturnData.BillPrefix && saleReturnData.FinYear 
      ? `${saleReturnData.BillPrefix}_${saleReturnData.BillNumber}_${saleReturnData.FinYear}`
      : `CreditNote_${saleReturnData.BillNumber}`;
    doc.save(`${billNo}.pdf`);
    URL.revokeObjectURL(pdfUrl);
  }
};

export const getSaleReturnPDFBlob = async (saleReturnData, companyDetails, items) => {
  const doc = await generateSaleReturnPDF(saleReturnData, companyDetails, items);
  return doc.output('blob');
};

export const shareViaWhatsApp = async (saleReturnData, companyDetails, items, phoneNumber) => {
  try {
    const doc = await generateSaleReturnPDF(saleReturnData, companyDetails, items);
    const pdfBlob = doc.output('blob');
    
    const billNo = saleReturnData.BillPrefix && saleReturnData.FinYear 
      ? `${saleReturnData.BillPrefix}/${saleReturnData.BillNumber}/${saleReturnData.FinYear}`
      : saleReturnData.BillNumber;
    
    const message = `Hello ${saleReturnData.CustomerName || saleReturnData.MasterName},\n\nYour credit note ${billNo} for AED ${parseFloat(saleReturnData.TotalAmount || 0).toFixed(2)} is ready.\n\nThank you for your business!`;
    
    // For web sharing (if supported)
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [new File([pdfBlob], 'creditnote.pdf', { type: 'application/pdf' })] })) {
      const file = new File([pdfBlob], `CreditNote_${billNo.replace(/\//g, '_')}.pdf`, { type: 'application/pdf' });
      await navigator.share({
        title: `Credit Note ${billNo}`,
        text: message,
        files: [file]
      });
      return { success: true, method: 'native' };
    } else {
      // Fallback: Open WhatsApp Web with message (PDF needs to be sent manually)
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = phoneNumber 
        ? `https://wa.me/${phoneNumber.replace(/[^0-9]/g, '')}?text=${encodedMessage}`
        : `https://wa.me/?text=${encodedMessage}`;
      
      // Download PDF first
      const billFileName = `CreditNote_${billNo.replace(/\//g, '_')}.pdf`;
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = billFileName;
      link.click();
      URL.revokeObjectURL(url);
      
      // Then open WhatsApp
      window.open(whatsappUrl, '_blank');
      
      return { success: true, method: 'fallback', message: 'PDF downloaded. Please attach it manually in WhatsApp.' };
    }
  } catch (error) {
    console.error('Error sharing via WhatsApp:', error);
    return { success: false, error: error.message };
  }
};
